import { Link, useNavigate } from "react-router-dom";
import { askScenarios, askSuggestionOrder } from "../lib/data";
import { useMgQual, useMgQuant, useCrossCategory, useCromptonLens, useDrivers, useConnections } from "../lib/realdata";

export default function Home() {
  const nav = useNavigate();
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const quant = useMgQuant();
  const qual = useMgQual();
  const cross = useCrossCategory();
  const lens = useCromptonLens();
  const drivers = useDrivers();
  const connections = useConnections();
  const validatedThemes = connections?.latent_themes.filter(t => t.validated).length ?? 0;

  // Enterprisewide stats — common-driver count, Crompton white-space count, owned-strength count
  const xMed = cross ? cross.common_brand.reduce((s, p) => s + p.mg_mean, 0) / cross.common_brand.length : 0;
  const yMed = cross ? cross.common_brand.reduce((s, p) => s + p.fans_mean, 0) / cross.common_brand.length : 0;
  const commonBrandCount = cross
    ? cross.common_brand.filter(p => p.mg_mean >= xMed && p.fans_mean >= yMed).length
    : 0;
  const oppCount = lens
    ? (lens.categories.Fans.brand?.rows.filter(r => r.quadrant === "opportunity").length ?? 0)
      + (lens.categories.Fans.functional?.rows.filter(r => r.quadrant === "opportunity").length ?? 0)
      + (lens.categories.MG.brand?.rows.filter(r => r.quadrant === "opportunity").length ?? 0)
    : 0;
  const ownedCount = lens
    ? (lens.categories.Fans.brand?.rows.filter(r => r.quadrant === "owned_strength").length ?? 0)
      + (lens.categories.Fans.functional?.rows.filter(r => r.quadrant === "owned_strength").length ?? 0)
      + (lens.categories.MG.brand?.rows.filter(r => r.quadrant === "owned_strength").length ?? 0)
    : 0;
  const totalN = drivers
    ? drivers.categories.MG.n + drivers.categories.Fans.n
    : 0;

  // Dynamic hero based on real top theme from qual and real top brand
  const topTheme = qual?.theme_rank?.[0];
  const topDurabilityTheme = qual?.theme_rank.find(t =>
    /jar|durab|motor|handle|break/i.test(t.theme)
  );
  const heroPhrase = topDurabilityTheme
    ? `${topDurabilityTheme.theme.toLowerCase()} is showing up in ${topDurabilityTheme.respondents_mentioning} of ${qual?.meta.total_interviews} interviews`
    : "durability in Tier 2 is the recurring concern";

  return (
    <div className="stagger">
      <section className="home-hero">
        <div>
          <div className="greet">Good morning, Ananya</div>
          <h1>
            Across the {quant?.meta.total_respondents ?? 2233} respondents in your MG study, <em className="warn">{heroPhrase}</em>.
          </h1>
        </div>
        <div className="date">{today}</div>
      </section>

      <section className="ew-tile" onClick={() => nav("/connections")} style={{ cursor: "pointer" }}>
        <div className="ew-cell">
          <div className="k">Cross-category portfolio</div>
          <div className="v">{totalN > 0 ? totalN.toLocaleString() : "—"}</div>
          <div className="s">Respondents across MG ({drivers?.categories.MG.n.toLocaleString() ?? "—"}) and Fans ({drivers?.categories.Fans.n.toLocaleString() ?? "—"}) studies, analysed in parallel.</div>
        </div>
        <div className="ew-cell">
          <div className="k">Latent need-states</div>
          <div className="v">{validatedThemes}</div>
          <div className="s">Psychological need-states statistically validated in BOTH studies. The same consumer underneath very different products.</div>
        </div>
        <div className="ew-cell">
          <div className="k">Crompton positioning</div>
          <div className="v coral">{oppCount}<span style={{ fontSize: 14, color: "var(--ink-60)" }}> / {ownedCount}</span></div>
          <div className="s">White-space opportunities to claim · vs owned strengths to defend. Click for AI revelations.</div>
        </div>
      </section>

      <section className="home-asker">
        <div>
          <div className="lab">Ask Pulse anything about your brand</div>
          <div className="chips">
            {askSuggestionOrder.slice(0, 3).map((id) => {
              const sc = askScenarios[id];
              return <button key={id} className="chip" onClick={() => nav("/ask")}>{sc.shortLabel}</button>;
            })}
          </div>
        </div>
        <div className="cta">
          <button className="btn-primary" onClick={() => nav("/ask")}>
            Open Ask
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
          </button>
        </div>
      </section>

      <section className="home-priorities">
        <div className="pri-card alert" onClick={() => nav("/signal")}>
          <div className="top-row">
            <span className="tag coral"><span className="dot" />Active alert</span>
            <span className="num muted" style={{ fontSize: 11 }}>high</span>
          </div>
          <h3>Amazon Kolkata — suspected review manipulation</h3>
          <div className="body">Preethi rating jumped 4.2 to 4.7 in 6 days; 84% of new reviews are 5-star with generic language, 76% inside a 48-hour window. Signal is off.</div>
          <div className="foot">
            <span>Flagged 5h ago</span>
            <span className="go">Review evidence →</span>
          </div>
        </div>

        <div className="pri-card move" onClick={() => nav("/investigate")}>
          <div className="top-row">
            <span className="tag teal"><span className="dot" />Investigation ready</span>
            <span className="num muted" style={{ fontSize: 11 }}>0.92 conf</span>
          </div>
          <h3>Preethi NPS dropped 6 points in Q2. Pulse found why.</h3>
          <div className="body">Durability issue in the Lucknow and Gorakpur 25-34 segment. Confirmed across quant, qual, and Amazon reviews. Traceable to one SKU, one production quarter.</div>
          <div className="foot">
            <span>Concluded 2h ago</span>
            <span className="go">Open investigation →</span>
          </div>
        </div>

        <div className="pri-card upcoming" onClick={() => nav("/repository")}>
          <div className="top-row">
            <span className="tag amber"><span className="dot" />Coming up</span>
            <span className="num muted" style={{ fontSize: 11 }}>in 14 days</span>
          </div>
          <h3>Wave 3 fieldwork begins May 6.</h3>
          <div className="body">12 cities planned, n=1,800 target. Adding a new "after-sales experience" module to the durability section. Pulse will flag wave-on-wave drifts automatically.</div>
          <div className="foot">
            <span>Fieldwork plan drafted</span>
            <span className="go">Review plan →</span>
          </div>
        </div>
      </section>

      <section className="home-row">
        <div className="scorecard">
          <div className="hd">
            <h2>Your brand vs the category</h2>
            <Link to="/dashboards" className="btn-link">See full scoreboard →</Link>
          </div>
          <div className="metric-grid">
            <div className="mt">
              <div className="label">NPS · Preethi</div>
              <div className="big">+44</div>
              <div className="sub neg">▼ 6 pts vs Wave 1</div>
            </div>
            <div className="mt">
              <div className="label">Market share · Q2</div>
              <div className="big">22.4%</div>
              <div className="sub pos">▲ 0.8 pts vs Wave 1</div>
            </div>
            <div className="mt">
              <div className="label">Unaided awareness</div>
              <div className="big">78%</div>
              <div className="sub pos">▲ 2 pts vs Wave 1</div>
            </div>
          </div>

          <div className="compare">
            <b>Preethi</b>
            <div className="prog"><span style={{ width: "74%" }} /></div>
            <div className="val">+44</div>
          </div>
          <div className="compare">
            <b>Bajaj</b>
            <div className="prog"><span className="bajaj" style={{ width: "45%" }} /></div>
            <div className="val">+27</div>
          </div>
          <div className="compare">
            <b>Philips</b>
            <div className="prog"><span className="philips" style={{ width: "42%" }} /></div>
            <div className="val">+25</div>
          </div>
          <div className="compare">
            <b>Category</b>
            <div className="prog"><span style={{ width: "63%", background: "var(--ink-20)" }} /></div>
            <div className="val">+38</div>
          </div>
        </div>

        <div className="feed-card">
          <h2>What Pulse has been doing</h2>
          <ul>
            <li>
              <div>
                <div className="t">Completed investigation · Preethi NPS drop</div>
                <div className="d">6 steps, 3 data sources, 0.92 confidence. Handoff: Product QC.</div>
              </div>
              <div className="when">2h ago</div>
            </li>
            <li>
              <div>
                <div className="t">Flagged anomaly · Amazon Kolkata</div>
                <div className="d">Review manipulation pattern. Waiting on your call.</div>
              </div>
              <div className="when">5h ago</div>
            </li>
            <li>
              <div>
                <div className="t">Harmonised Wave 2 ingest</div>
                <div className="d">37 brand codes reconciled, 11 question labels. No values dropped.</div>
              </div>
              <div className="when">yesterday</div>
            </li>
            <li>
              <div>
                <div className="t">Saved view · Priya (Brand Strategy)</div>
                <div className="d">"Category NPS by city, wave-on-wave" shared with your team.</div>
              </div>
              <div className="when">2d ago</div>
            </li>
            <li>
              <div>
                <div className="t">Added social feed · Flipkart</div>
                <div className="d">Product listings for Preethi, Bajaj, Philips now tracked hourly.</div>
              </div>
              <div className="when">3d ago</div>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}
